export interface BusinessScopedStockEvent {
  business_id?: string | null;
  payload?: {
    business_id?: string | null;
  } | null;
}

function cleanId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.trim();
  return clean || null;
}

/**
 * Resolve the stock-event site exactly as the canonical projector does.
 * Conflicting top-level/payload coordinates are never eligible as metadata
 * sources, even when one of them happens to match the active site.
 */
export function stockEventBusinessId(
  event: BusinessScopedStockEvent,
): { businessId: string | null; mismatch: boolean } {
  const topLevel = cleanId(event.business_id);
  const payload = cleanId(event.payload?.business_id);
  return {
    businessId: topLevel ?? payload,
    mismatch:
      topLevel !== null
      && payload !== null
      && topLevel !== payload,
  };
}

export function stockEventMatchesBusiness(
  event: BusinessScopedStockEvent,
  businessId: string | null,
  allowLegacyUnscoped: boolean,
): boolean {
  const resolved = stockEventBusinessId(event);
  if (resolved.mismatch) return false;
  if (businessId === null) return resolved.businessId === null;
  return resolved.businessId === businessId
    || (allowLegacyUnscoped && resolved.businessId === null);
}

/**
 * Exact-site evidence always wins, regardless of input ordering. A legacy
 * unscoped event is considered only for a proven single-active-site tenant.
 */
export function findExactThenLegacyStockEvent<
  Event extends BusinessScopedStockEvent,
>(
  events: readonly Event[],
  businessId: string,
  allowLegacyUnscoped: boolean,
  predicate: (event: Event) => boolean,
): Event | undefined {
  const matching = events.filter(predicate);
  const exact = matching.find((event) => {
    const resolved = stockEventBusinessId(event);
    return !resolved.mismatch && resolved.businessId === businessId;
  });
  if (exact || !allowLegacyUnscoped) return exact;
  return matching.find((event) => {
    const resolved = stockEventBusinessId(event);
    return !resolved.mismatch && resolved.businessId === null;
  });
}
